import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AdminData } from '../../_models/admin';
import { CurrencyData } from '../../_models/currency';
import { Counts } from '../../_models/data';
import { HrAnnouncementData, HrDepartmentData, HrDesignationData, HrExpenseData, HrLeaveData, HrOrganogramData, HrPayrollData, HrPerformanceData, HrReportData, HrStaffBankAccountData, HrStaffData } from '../../_models/hr';
import { ApiService } from '../../_services/api.service';
import { ChecksService } from '../../_services/checks.service';
import { NotificationService } from '../../_services/notification.service';

interface HrPeopleSection {
  slug: string;
  title: string;
  summary: string;
}

@Component({
  selector: 'app-hr-people',
  templateUrl: './people.component.html',
  styleUrl: './people.component.css'
})
export class HrPeopleComponent implements OnInit, OnDestroy {
  sections: HrPeopleSection[] = [
    { slug: 'payroll-tax', title: 'Payroll & tax (Nigerian)', summary: 'Manage Nigerian payroll runs, PAYE, pension, and statutory tax records.' },
    { slug: 'leave-management', title: 'Leave management', summary: 'Track leave requests, balances, calendars, and approvals.' },
    { slug: 'employee-profiles-kin', title: 'Employee profiles & KIN', summary: 'Maintain staff records, next of kin details, employment data, and documents.' },
    { slug: 'performance-epa', title: 'Performance & EPA portal', summary: 'Coordinate performance reviews, EPA submissions, goals, and manager feedback.' },
    { slug: 'expenses-approvals', title: 'Expenses & approvals', summary: 'Review staff expenses, reimbursements, approval status, and audit trails.' },
    { slug: 'onboarding-email-setup', title: 'Onboarding & email setup', summary: 'Prepare new starter checklists, account setup, and welcome communications.' },
    { slug: 'announcements-reports', title: 'Announcements & reports', summary: 'Publish HR announcements and generate staff-facing or management reports.' },
    { slug: 'departments', title: 'Departments', summary: 'Create, edit, and manage departments used across staff profiles and organogram records.' },
    { slug: 'designations', title: 'Designations', summary: 'Create, edit, and manage staff designations used in profiles and reporting lines.' },
    { slug: 'org-chart', title: 'Org chart', summary: 'View reporting lines, departments, teams, and role ownership.' },
    { slug: 'staff-bank-accounts', title: 'Staff bank accounts', summary: 'Manage employee bank account records used for payroll and reimbursements.' },
  ];

  activeSection: HrPeopleSection = this.sections[0];
  private routeSubscription: Subscription;
  currentUser: AdminData;

  page: number = 1;
  count: Counts;
  loading: boolean = true;
  processing: boolean = false;

  staffList: HrStaffData[] = [];
  departmentList: HrDepartmentData[] = [];
  designationList: HrDesignationData[] = [];
  organogramList: HrOrganogramData[] = [];
  bankAccountList: HrStaffBankAccountData[] = [];
  leaveList: HrLeaveData[] = [];
  calendarLeaveList: HrLeaveData[] = [];
  performanceList: HrPerformanceData[] = [];
  payrollList: HrPayrollData[] = [];
  expenseList: HrExpenseData[] = [];
  announcementList: HrAnnouncementData[] = [];
  hrReport: HrReportData;
  activeCurrencies: CurrencyData[] = [];
  selectedStaff: HrStaffData = null;
  selectedStaffOrganogram: HrOrganogramData = null;
  selectedStaffLoading: boolean = false;
  selectedStaffTab: string = 'overview';

  staffEdit: boolean = false;
  departmentEdit: boolean = false;
  designationEdit: boolean = false;
  leaveEdit: boolean = false;
  performanceEdit: boolean = false;
  payrollEdit: boolean = false;
  expenseEdit: boolean = false;
  expenseSearchMode: boolean = false;
  announcementEdit: boolean = false;
  showNewStaffDesignation: boolean = false;
  showNewOrganogramDesignation: boolean = false;
  bankEdit: boolean = false;
  calendarDate: Date = new Date();
  selectedOnboardingStaffRef: string = '';
  directorySearch: string = '';
  directoryDepartmentFilter: string = '';
  directoryRoleFilter: string = '';
  leaveTypes: string[] = ['Annual Leave', 'Sick Leave', 'Compassionate Leave', 'Maternity Leave', 'Paternity Leave', 'Study Leave', 'Unpaid Leave'];
  expenseCategories: string[] = ['Travel', 'Meals', 'Transport', 'Internet', 'Office supplies', 'Training', 'Medical', 'Other'];
  onboardingItems = [
    { key: 'createdEmail', label: 'Created Google Workspace email' },
    { key: 'createdAdmin', label: 'Created admin credential' },
    { key: 'updatedProfile', label: 'Updated profile' },
    { key: 'updatedAddress', label: 'Updated address' },
    { key: 'updatedIce', label: 'Updated ICE information' },
    { key: 'updatedKin', label: 'Updated next of kin' },
    { key: 'updatedAccount', label: 'Updated bank account' },
    { key: 'uploadedPassport', label: 'Uploaded passport photograph' },
    { key: 'uploadedResume', label: 'Uploaded resume' },
  ];

  staffForm = this.fb.group({
    ref: [0],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    otherName: [''],
    email: ['', [Validators.required, Validators.email]],
    c_email: ['', Validators.email],
    phone: [''],
    dateOfBirth: [''],
    jobTitle: ['', Validators.required],
    department: ['', Validators.required],
    employmentType: ['Full time'],
    hireDate: [''],
    country: [''],
    countries: [''],
    profile: [''],
    address: [''],
    iceName: [''],
    icePhone: [''],
    nextOfKinName: [''],
    nextOfKinPhone: [''],
  });

  departmentForm = this.fb.group({
    ref: [0],
    title: ['', Validators.required],
    description: [''],
    country: [''],
  });

  designationForm = this.fb.group({
    ref: [0],
    title: ['', Validators.required],
    description: [''],
    country: [''],
  });

  staffDesignationForm = this.fb.group({
    title: ['', Validators.required],
  });

  organogramDesignationForm = this.fb.group({
    title: ['', Validators.required],
  });

  bankForm = this.fb.group({
    ref: [0],
    staff: ['', Validators.required],
    bankName: ['', Validators.required],
    accountName: ['', Validators.required],
    accountNumber: ['', Validators.required],
    accountType: ['Savings'],
    bankCode: [''],
    currency: ['NGN'],
  });

  leaveForm = this.fb.group({
    ref: [0],
    staff: ['', Validators.required],
    leaveType: ['Annual Leave', Validators.required],
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    reason: [''],
    status: ['pending'],
    country: [''],
  });

  organogramForm = this.fb.group({
    staff: ['', Validators.required],
    role: ['', Validators.required],
    department: [''],
    directBoss: [''],
  });

  performanceForm = this.fb.group({
    ref: [0],
    staff: ['', Validators.required],
    cycleTitle: ['', Validators.required],
    periodStart: ['', Validators.required],
    periodEnd: ['', Validators.required],
    goals: [''],
    selfReview: [''],
    managerReview: [''],
    score: [0],
    rating: [''],
    status: ['draft'],
    country: [''],
  });

  payrollForm = this.fb.group({
    ref: [0],
    staff: ['', Validators.required],
    periodTitle: ['', Validators.required],
    periodStart: ['', Validators.required],
    periodEnd: ['', Validators.required],
    grossPay: [0, Validators.required],
    allowances: [0],
    bonuses: [0],
    payeTax: [0],
    pension: [0],
    nhf: [0],
    nhis: [0],
    otherDeductions: [0],
    currency: ['NGN'],
    status: ['draft'],
    country: ['NG'],
    notes: [''],
  });

  expenseForm = this.fb.group({
    ref: [0],
    staff: ['', Validators.required],
    expenseDate: ['', Validators.required],
    category: ['Travel', Validators.required],
    description: ['', Validators.required],
    amount: [0, Validators.required],
    currency: ['NGN'],
    receiptUrl: [''],
    status: ['pending'],
    decisionNote: [''],
    country: [''],
  });

  expenseSearchForm = this.fb.group({
    q: [''],
    staff: [''],
    status: [''],
    category: [''],
    dateFrom: [''],
    dateTo: [''],
  });

  announcementForm = this.fb.group({
    ref: [0],
    title: ['', Validators.required],
    body: ['', Validators.required],
    pinned: [false],
    status: ['published'],
    country: [''],
  });

  reportFilterForm = this.fb.group({
    period: ['month'],
    dateFrom: [''],
    dateTo: [''],
  });

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private apiService: ApiService,
    public checkService: ChecksService,
    private notifyService: NotificationService,
  ) {
    this.checkService.checkSession();
  }

  ngOnInit(): void {
    this.currentUser = this.checkService.getAdminData();
    this.getActiveCurrencies();
    this.setReportPeriod('month', false);
    if (this.isStaffOnly()) {
      this.leaveForm.get('staff').clearValidators();
      this.leaveForm.get('staff').setValue('0');
      this.leaveForm.get('staff').updateValueAndValidity();
      this.expenseForm.get('staff').clearValidators();
      this.expenseForm.get('staff').setValue('0');
      this.expenseForm.get('staff').updateValueAndValidity();
    }

    this.routeSubscription = this.route.paramMap.subscribe(params => {
      const sectionSlug = params.get('section') || this.sections[0].slug;
      this.activeSection = this.sections.find(section => section.slug === sectionSlug) || this.sections[0];
      this.loadHrData();
    });
  }

  getActiveCurrencies(): void {
    this.apiService.getActiveCurrencies().subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success == true) {
        this.activeCurrencies = data.data || [];
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  loadHrData(): void {
    if (this.isStaffOnly()) {
      this.getLeaves();
      this.getExpenses();
      this.getAnnouncements();
      return;
    }

    this.getStaff();
    this.getDepartments();
    this.getDesignations();
    this.getOrganogram();
    this.getBankAccounts();
    this.getLeaves();
    this.getPerformanceReviews();
    this.getPayrollRecords();
    this.getExpenses();
    this.getAnnouncements();
    if (this.canViewHrReports()) {
      this.getHrReport();
    }
  }

  getStaff(page:number = 1): void {
    this.loading = true;
    this.apiService.getHrStaff(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.loading = false;
      if (data.success === true) {
        this.count = data.counts;
        this.staffList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load staff');
      }
    });
  }

  getBankAccounts(page:number = 1): void {
    this.apiService.getHrStaffBankAccountList(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.bankAccountList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load bank accounts');
      }
    });
  }

  getLeaves(page:number = 1): void {
    this.apiService.getHrLeaves(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.leaveList = data.data || [];
        this.getLeaveCalendar();
      } else {
        this.showApiError(data, 'Unable to load leave bookings');
      }
    });
  }

  getPerformanceReviews(page:number = 1): void {
    this.apiService.getHrPerformance(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.performanceList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load performance reviews');
      }
    });
  }

  getPayrollRecords(page:number = 1): void {
    this.apiService.getHrPayroll(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.payrollList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load payroll records');
      }
    });
  }

  getExpenses(page:number = 1): void {
    const request = this.expenseSearchMode ? this.apiService.searchHrExpenses(this.expenseSearchForm.value, page) : this.apiService.getHrExpenses(page);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.expenseList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load expense requests');
      }
    });
  }

  getAnnouncements(page:number = 1): void {
    this.apiService.getHrAnnouncements(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.announcementList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load announcements');
      }
    });
  }

  getHrReport(): void {
    this.apiService.getHrReportSummary(this.reportFilterForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.hrReport = data.data;
      } else {
        this.showApiError(data, 'Unable to load HR report');
      }
    });
  }

  getLeaveCalendar(): void {
    const range = this.calendarRange();
    this.apiService.getHrLeaveCalendar({
      dateFrom: this.formatDate(range.first),
      dateTo: this.formatDate(range.last),
    }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.calendarLeaveList = data.data || [];
      } else {
        this.calendarLeaveList = this.leaveList;
      }
    });
  }

  getDepartments(page:number = 1): void {
    this.apiService.getHrDepartments(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.departmentList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load departments');
      }
    });
  }

  getDesignations(page:number = 1): void {
    this.apiService.getHrDesignations(page).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.designationList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load designations');
      }
    });
  }

  getOrganogram(): void {
    this.apiService.getHrOrganogram().subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.organogramList = data.data || [];
      } else {
        this.showApiError(data, 'Unable to load organogram');
      }
    });
  }

  saveStaff(): void {
    if (this.staffForm.invalid) {
      this.staffForm.markAllAsTouched();
      return;
    }

    const formValue = this.staffForm.value;
    const payload = {
      ref: formValue.ref,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      otherName: formValue.otherName,
      email: formValue.email,
      c_email: formValue.c_email,
      phone: formValue.phone,
      jobTitle: formValue.jobTitle,
      department: formValue.department,
      employmentType: formValue.employmentType,
      hireDate: formValue.hireDate,
      country: formValue.country,
      countries: this.splitCsv(formValue.countries),
      profile: { notes: formValue.profile, dateOfBirth: formValue.dateOfBirth },
      address: { addressLine1: formValue.address },
      ice: { name: formValue.iceName, phone: formValue.icePhone },
      nextOfKin: { name: formValue.nextOfKinName, phone: formValue.nextOfKinPhone },
    };

    this.processing = true;
    const request = this.staffEdit ? this.apiService.editHrStaff(payload) : this.apiService.createHrStaff(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Staff record saved successfully', 'HR & People');
        this.closeModal('staffFormModal');
        this.cancelStaffEdit();
        this.getStaff(this.page);
      } else {
        this.showApiError(data, 'Unable to save staff');
      }
    });
  }

  editStaff(staff: HrStaffData): void {
    this.staffEdit = true;
    this.staffForm.patchValue({
      ref: staff.ref,
      firstName: staff.firstName,
      lastName: staff.lastName,
      otherName: staff.otherName,
      email: staff.email,
      c_email: staff.c_email,
      phone: staff.phone,
      dateOfBirth: staff.profile?.dateOfBirth || '',
      jobTitle: staff.jobTitle,
      department: String(staff.department || ''),
      employmentType: staff.employmentType || 'Full time',
      hireDate: staff.hireDate,
      country: staff.country,
      countries: (staff.countries || []).join(', '),
      profile: staff.profile?.summary || staff.profile?.notes || '',
      address: staff.address?.address || staff.address?.addressLine1 || '',
      iceName: staff.ice?.name || '',
      icePhone: staff.ice?.phone || '',
      nextOfKinName: staff.nextOfKin?.name || '',
      nextOfKinPhone: staff.nextOfKin?.phone || '',
    });
    this.openModal('staffFormModal');
  }

  cancelStaffEdit(): void {
    this.staffEdit = false;
    this.staffForm.reset({
      ref: 0,
      employmentType: 'Full time',
    });
  }

  removeStaff(staff: HrStaffData): void {
    if (!confirm('Remove '+this.staffName(staff)+' from staff records?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrStaff(staff.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Staff record removed', 'HR & People');
        this.getStaff(this.page);
      } else {
        this.showApiError(data, 'Unable to remove staff');
      }
    });
  }

  suspendStaff(staff: HrStaffData): void {
    if (!confirm('Suspend '+this.staffName(staff)+'?')) {
      return;
    }

    this.processing = true;
    this.apiService.suspendHrStaff(staff.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Staff record suspended', 'HR & People');
        this.getStaff(this.page);
      } else {
        this.showApiError(data, 'Unable to suspend staff');
      }
    });
  }

  uploadPassport(event: Event, staff: HrStaffData): void {
    const file = this.getFile(event);
    if (!file) {
      return;
    }

    this.processing = true;
    this.apiService.uploadHrStaffPassport(staff.ref, file).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Passport photograph uploaded', 'HR & People');
        this.getStaff(this.page);
      } else {
        this.showApiError(data, 'Unable to upload passport');
      }
    });
  }

  uploadResume(event: Event, staff: HrStaffData): void {
    const file = this.getFile(event);
    if (!file) {
      return;
    }

    this.processing = true;
    this.apiService.uploadHrStaffResume(staff.ref, file).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Resume uploaded', 'HR & People');
        this.getStaff(this.page);
      } else {
        this.showApiError(data, 'Unable to upload resume');
      }
    });
  }

  saveLeave(): void {
    if (this.leaveForm.invalid) {
      this.leaveForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const payload: any = this.leaveForm.value;
    if (this.isStaffOnly()) {
      payload.staff = 0;
      payload.status = 'pending';
      payload.country = '';
    }
    const request = this.leaveEdit ? this.apiService.editHrLeave(payload) : this.apiService.createHrLeave(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Leave booking saved successfully', 'HR & People');
        this.closeModal('leaveFormModal');
        this.cancelLeaveEdit();
        this.getLeaves();
      } else {
        this.showApiError(data, 'Unable to save leave booking');
      }
    });
  }

  editLeave(leave: HrLeaveData): void {
    if (this.isStaffOnly()) {
      return;
    }

    this.leaveEdit = true;
    this.leaveForm.patchValue({
      ref: leave.ref,
      staff: String(this.leaveStaffRef(leave) || ''),
      leaveType: leave.leaveType || 'Annual Leave',
      startDate: leave.startDate,
      endDate: leave.endDate,
      reason: leave.reason,
      status: String(leave.status || 'pending'),
      country: leave.country,
    });
    this.openModal('leaveFormModal');
  }

  cancelLeaveEdit(): void {
    this.leaveEdit = false;
    this.leaveForm.reset({
      ref: 0,
      staff: this.isStaffOnly() ? '0' : '',
      leaveType: 'Annual Leave',
      status: 'pending',
    });
  }

  changeLeaveStatus(leave: HrLeaveData, status: string): void {
    this.processing = true;
    this.apiService.changeHrLeaveStatus({ ref: leave.ref, status }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Leave status updated', 'HR & People');
        this.getLeaves();
      } else {
        this.showApiError(data, 'Unable to update leave status');
      }
    });
  }

  removeLeave(leave: HrLeaveData): void {
    if (!confirm('Remove this leave booking?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrLeave(leave.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Leave booking removed', 'HR & People');
        this.getLeaves();
      } else {
        this.showApiError(data, 'Unable to remove leave booking');
      }
    });
  }

  savePerformanceReview(): void {
    if (this.performanceForm.invalid) {
      this.performanceForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const payload = this.performanceForm.value;
    const request = this.performanceEdit ? this.apiService.editHrPerformance(payload) : this.apiService.createHrPerformance(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Performance review saved successfully', 'HR & People');
        this.closeModal('performanceFormModal');
        this.cancelPerformanceEdit();
        this.getPerformanceReviews();
      } else {
        this.showApiError(data, 'Unable to save performance review');
      }
    });
  }

  editPerformanceReview(review: HrPerformanceData): void {
    this.performanceEdit = true;
    this.performanceForm.patchValue({
      ref: review.ref,
      staff: String(this.performanceStaffRef(review) || ''),
      cycleTitle: review.cycleTitle,
      periodStart: review.periodStart,
      periodEnd: review.periodEnd,
      goals: review.goals,
      selfReview: review.selfReview,
      managerReview: review.managerReview,
      score: review.score || 0,
      rating: review.rating,
      status: String(review.status || 'draft'),
      country: review.country,
    });
    this.openModal('performanceFormModal');
  }

  cancelPerformanceEdit(): void {
    this.performanceEdit = false;
    this.performanceForm.reset({
      ref: 0,
      status: 'draft',
      score: 0,
    });
  }

  changePerformanceStatus(review: HrPerformanceData, status: string): void {
    this.processing = true;
    this.apiService.changeHrPerformanceStatus({ ref: review.ref, status }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Performance review updated', 'HR & People');
        this.getPerformanceReviews();
      } else {
        this.showApiError(data, 'Unable to update performance review');
      }
    });
  }

  removePerformanceReview(review: HrPerformanceData): void {
    if (!confirm('Remove this performance review?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrPerformance(review.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Performance review removed', 'HR & People');
        this.getPerformanceReviews();
      } else {
        this.showApiError(data, 'Unable to remove performance review');
      }
    });
  }

  savePayrollRecord(): void {
    if (this.payrollForm.invalid) {
      this.payrollForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const payload = this.payrollForm.value;
    const request = this.payrollEdit ? this.apiService.editHrPayroll(payload) : this.apiService.createHrPayroll(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Payroll record saved successfully', 'HR & People');
        this.closeModal('payrollFormModal');
        this.cancelPayrollEdit();
        this.getPayrollRecords();
      } else {
        this.showApiError(data, 'Unable to save payroll record');
      }
    });
  }

  editPayrollRecord(record: HrPayrollData): void {
    this.payrollEdit = true;
    this.payrollForm.patchValue({
      ref: record.ref,
      staff: String(this.payrollStaffRef(record) || ''),
      periodTitle: record.periodTitle,
      periodStart: record.periodStart,
      periodEnd: record.periodEnd,
      grossPay: record.grossPay || 0,
      allowances: record.allowances || 0,
      bonuses: record.bonuses || 0,
      payeTax: record.payeTax || 0,
      pension: record.pension || 0,
      nhf: record.nhf || 0,
      nhis: record.nhis || 0,
      otherDeductions: record.otherDeductions || 0,
      currency: record.currency || 'NGN',
      status: String(record.status || 'draft'),
      country: record.country || 'NG',
      notes: record.notes,
    });
    this.openModal('payrollFormModal');
  }

  cancelPayrollEdit(): void {
    this.payrollEdit = false;
    this.payrollForm.reset({
      ref: 0,
      grossPay: 0,
      allowances: 0,
      bonuses: 0,
      payeTax: 0,
      pension: 0,
      nhf: 0,
      nhis: 0,
      otherDeductions: 0,
      currency: 'NGN',
      status: 'draft',
      country: 'NG',
    });
  }

  changePayrollStatus(record: HrPayrollData, status: string): void {
    this.processing = true;
    this.apiService.changeHrPayrollStatus({ ref: record.ref, status }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Payroll status updated', 'HR & People');
        this.getPayrollRecords();
      } else {
        this.showApiError(data, 'Unable to update payroll status');
      }
    });
  }

  removePayrollRecord(record: HrPayrollData): void {
    if (!confirm('Remove this payroll record?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrPayroll(record.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Payroll record removed', 'HR & People');
        this.getPayrollRecords();
      } else {
        this.showApiError(data, 'Unable to remove payroll record');
      }
    });
  }

  searchExpenses(): void {
    this.expenseSearchMode = true;
    this.getExpenses();
  }

  resetExpenseSearch(): void {
    this.expenseSearchMode = false;
    this.expenseSearchForm.reset();
    this.getExpenses();
  }

  saveExpense(): void {
    if (this.expenseForm.invalid) {
      this.expenseForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const payload: any = this.expenseForm.value;
    if (this.isStaffOnly()) {
      payload.staff = 0;
      payload.status = 'pending';
      payload.decisionNote = '';
      payload.country = '';
    }
    const request = this.expenseEdit ? this.apiService.editHrExpense(payload) : this.apiService.createHrExpense(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Expense request saved successfully', 'HR & People');
        this.closeModal('expenseFormModal');
        this.cancelExpenseEdit();
        this.getExpenses();
      } else {
        this.showApiError(data, 'Unable to save expense request');
      }
    });
  }

  editExpense(expense: HrExpenseData): void {
    if (this.isStaffOnly()) {
      return;
    }

    this.expenseEdit = true;
    this.expenseForm.patchValue({
      ref: expense.ref,
      staff: String(this.expenseStaffRef(expense) || ''),
      expenseDate: expense.expenseDate,
      category: expense.category || 'Travel',
      description: expense.description,
      amount: expense.amount || 0,
      currency: expense.currency || 'NGN',
      receiptUrl: expense.receiptUrl,
      status: String(expense.status || 'pending'),
      decisionNote: expense.decisionNote,
      country: expense.country,
    });
    this.openModal('expenseFormModal');
  }

  cancelExpenseEdit(): void {
    this.expenseEdit = false;
    this.expenseForm.reset({
      ref: 0,
      staff: this.isStaffOnly() ? '0' : '',
      category: 'Travel',
      amount: 0,
      currency: 'NGN',
      status: 'pending',
    });
  }

  changeExpenseStatus(expense: HrExpenseData, status: string): void {
    this.processing = true;
    this.apiService.changeHrExpenseStatus({ ref: expense.ref, status }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Expense status updated', 'HR & People');
        this.getExpenses();
      } else {
        this.showApiError(data, 'Unable to update expense status');
      }
    });
  }

  removeExpense(expense: HrExpenseData): void {
    if (!confirm('Remove this expense request?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrExpense(expense.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Expense request removed', 'HR & People');
        this.getExpenses();
      } else {
        this.showApiError(data, 'Unable to remove expense request');
      }
    });
  }

  saveAnnouncement(): void {
    if (this.announcementForm.invalid) {
      this.announcementForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const request = this.announcementEdit ? this.apiService.editHrAnnouncement(this.announcementForm.value) : this.apiService.createHrAnnouncement(this.announcementForm.value);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Announcement saved successfully', 'HR & People');
        this.closeModal('announcementFormModal');
        this.cancelAnnouncementEdit();
        this.getAnnouncements();
      } else {
        this.showApiError(data, 'Unable to save announcement');
      }
    });
  }

  editAnnouncement(announcement: HrAnnouncementData): void {
    this.announcementEdit = true;
    this.announcementForm.patchValue({
      ref: announcement.ref,
      title: announcement.title,
      body: announcement.body,
      pinned: announcement.pinned,
      status: String(announcement.status || 'published'),
      country: announcement.country,
    });
    this.openModal('announcementFormModal');
  }

  cancelAnnouncementEdit(): void {
    this.announcementEdit = false;
    this.announcementForm.reset({
      ref: 0,
      pinned: false,
      status: 'published',
    });
  }

  publishAnnouncement(announcement: HrAnnouncementData): void {
    this.processing = true;
    this.apiService.publishHrAnnouncement({ ref: announcement.ref }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Announcement published', 'HR & People');
        this.getAnnouncements();
      } else {
        this.showApiError(data, 'Unable to publish announcement');
      }
    });
  }

  removeAnnouncement(announcement: HrAnnouncementData): void {
    if (!confirm('Remove this announcement?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrAnnouncement(announcement.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Announcement removed', 'HR & People');
        this.getAnnouncements();
      } else {
        this.showApiError(data, 'Unable to remove announcement');
      }
    });
  }

  saveDepartment(): void {
    if (this.departmentForm.invalid) {
      this.departmentForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const payload = this.departmentForm.value;
    const request = this.departmentEdit ? this.apiService.editHrDepartment(payload) : this.apiService.createHrDepartment(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Department saved successfully', 'HR & People');
        this.closeModal('departmentFormModal');
        this.cancelDepartmentEdit();
        this.getDepartments();
      } else {
        this.showApiError(data, 'Unable to save department');
      }
    });
  }

  editDepartment(department: HrDepartmentData): void {
    this.departmentEdit = true;
    this.departmentForm.patchValue({
      ref: department.ref,
      title: department.title,
      description: department.description,
      country: department.country,
    });
    this.openModal('departmentFormModal');
  }

  cancelDepartmentEdit(): void {
    this.departmentEdit = false;
    this.departmentForm.reset({ ref: 0 });
  }

  removeDepartment(department: HrDepartmentData): void {
    if (!confirm('Remove '+department.title+' department?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrDepartment(department.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Department removed', 'HR & People');
        this.getDepartments();
      } else {
        this.showApiError(data, 'Unable to remove department');
      }
    });
  }

  saveDesignation(): void {
    if (this.designationForm.invalid) {
      this.designationForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const payload = this.designationForm.value;
    const request = this.designationEdit ? this.apiService.editHrDesignation(payload) : this.apiService.createHrDesignation(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Designation saved successfully', 'HR & People');
        this.closeModal('designationFormModal');
        this.cancelDesignationEdit();
        this.getDesignations();
      } else {
        this.showApiError(data, 'Unable to save designation');
      }
    });
  }

  editDesignation(designation: HrDesignationData): void {
    this.designationEdit = true;
    this.designationForm.patchValue({
      ref: designation.ref,
      title: designation.title,
      description: designation.description,
      country: designation.country,
    });
    this.openModal('designationFormModal');
  }

  cancelDesignationEdit(): void {
    this.designationEdit = false;
    this.designationForm.reset({ ref: 0 });
  }

  removeDesignation(designation: HrDesignationData): void {
    if (!confirm('Remove '+designation.title+' designation?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrDesignation(designation.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Designation removed', 'HR & People');
        this.getDesignations();
      } else {
        this.showApiError(data, 'Unable to remove designation');
      }
    });
  }

  createStaffDesignation(): void {
    this.createInlineDesignation(this.staffDesignationForm.value.title, 'staff');
  }

  createOrganogramDesignation(): void {
    this.createInlineDesignation(this.organogramDesignationForm.value.title, 'organogram');
  }

  private createInlineDesignation(title: string, target: 'staff' | 'organogram'): void {
    title = String(title || '').trim();
    if (title === '') {
      return;
    }

    this.processing = true;
    this.apiService.createHrDesignation({ title }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        const designationTitle = data.data?.title || title;
        this.notifyService.showSuccess('Designation created successfully', 'HR & People');
        this.getDesignations();
        if (target === 'staff') {
          this.staffForm.patchValue({ jobTitle: designationTitle });
          this.staffDesignationForm.reset();
          this.showNewStaffDesignation = false;
        } else {
          this.organogramForm.patchValue({ role: designationTitle });
          this.organogramDesignationForm.reset();
          this.showNewOrganogramDesignation = false;
        }
      } else {
        this.showApiError(data, 'Unable to create designation');
      }
    });
  }

  saveBankAccount(): void {
    if (this.bankForm.invalid) {
      this.bankForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    const payload = this.bankForm.value;
    const request = this.bankEdit ? this.apiService.editHrStaffBankAccount(payload) : this.apiService.createHrStaffBankAccount(payload);
    request.subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Bank account saved successfully', 'HR & People');
        this.closeModal('bankFormModal');
        this.cancelBankEdit();
        this.getBankAccounts();
      } else {
        this.showApiError(data, 'Unable to save bank account');
      }
    });
  }

  editBankAccount(account: HrStaffBankAccountData, staff?: HrStaffData): void {
    this.bankEdit = true;
    this.bankForm.patchValue({
      ref: account.ref,
      staff: String(staff?.ref || this.bankStaffRef(account) || ''),
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      accountType: account.accountType || 'Savings',
      bankCode: account.bankCode,
      currency: account.currency || 'NGN',
    });
    this.openModal('bankFormModal');
  }

  cancelBankEdit(): void {
    this.bankEdit = false;
    this.bankForm.reset({
      ref: 0,
      accountType: 'Savings',
      currency: 'NGN',
    });
  }

  removeBankAccount(account: HrStaffBankAccountData): void {
    if (!confirm('Remove this bank account?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteHrStaffBankAccount(account.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Bank account removed', 'HR & People');
        this.getBankAccounts();
      } else {
        this.showApiError(data, 'Unable to remove bank account');
      }
    });
  }

  saveOrganogram(): void {
    if (this.organogramForm.invalid) {
      this.organogramForm.markAllAsTouched();
      return;
    }

    this.processing = true;
    this.apiService.setHrOrganogram(this.organogramForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.processing = false;
      if (data.success === true) {
        this.notifyService.showSuccess('Organogram updated successfully', 'HR & People');
        this.closeModal('organogramFormModal');
        this.organogramForm.reset();
        this.getOrganogram();
      } else {
        this.showApiError(data, 'Unable to update organogram');
      }
    });
  }

  staffName(staff: HrStaffData): string {
    if (!staff) {
      return '';
    }

    return staff['name'] || [staff.firstName, staff.lastName].filter(Boolean).join(' ') || staff.email || 'Staff';
  }

  openStaffDrawer(staff: HrStaffData): void {
    if (!staff || !staff.ref) {
      return;
    }

    this.selectedStaff = staff;
    this.selectedStaffOrganogram = null;
    this.selectedStaffTab = 'overview';
    this.selectedStaffLoading = true;

    this.apiService.getOneHrStaff(staff.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.selectedStaffLoading = false;
      if (data.success === true) {
        this.selectedStaff = data.data;
      } else {
        this.showApiError(data, 'Unable to load staff details');
      }
    });

    this.apiService.getHrOrganogramStaff(staff.ref).subscribe(data => {
      this.checkService.checkLoggedin(data);
      this.selectedStaffOrganogram = data.success === true ? data.data : null;
    });
  }

  closeStaffDrawer(): void {
    this.selectedStaff = null;
    this.selectedStaffOrganogram = null;
    this.selectedStaffTab = 'overview';
    this.selectedStaffLoading = false;
  }

  setStaffDrawerTab(tab: string): void {
    this.selectedStaffTab = tab;
  }

  selectedStaffReports(): any[] {
    return this.selectedStaffOrganogram?.directReports || [];
  }

  selectedStaffDepartment(): string {
    const department: any = this.selectedStaffOrganogram?.department || this.selectedStaff?.department;
    return this.getDepartmentTitle(department) || '-';
  }

  selectedStaffManagerName(): string {
    return this.staffName(this.selectedStaffOrganogram?.directBoss || this.selectedStaffOrganogram?.supervisor);
  }

  selectedStaffInitials(staff: HrStaffData = this.selectedStaff): string {
    const names = this.staffName(staff).split(' ').filter(Boolean);
    return (names.length > 1 ? names[0].charAt(0) + names[names.length - 1].charAt(0) : this.staffName(staff).slice(0, 2)).toUpperCase();
  }

  canViewFullStaffProfile(staff: HrStaffData = this.selectedStaff): boolean {
    if (!staff) {
      return false;
    }

    const adminRef = Number(staff.admin?.ref || 0);
    return this.checkService.canRead(['hr.staff', 'users'])
      || this.checkService.canModify(['hr.staff', 'users'])
      || (adminRef > 0 && adminRef === Number(this.currentUser?.ref || 0));
  }

  drawerDocumentByType(type: string): any {
    return this.staffDocumentByType(this.selectedStaff, type);
  }

  documentUrl(document: any): string {
    const filePath = String(document?.filePath || '');
    return /^https?:\/\//i.test(filePath) ? filePath : '';
  }

  displayValue(value: any): string {
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  filteredStaffList(): HrStaffData[] {
    const search = this.directorySearch.trim().toLowerCase();
    return (this.staffList || []).filter(staff => {
      const staffName = this.staffName(staff).toLowerCase();
      const email = String(staff.email || '').toLowerCase();
      const role = String(staff.jobTitle || '').toLowerCase();
      const department = String(staff.department || '').toLowerCase();
      const departmentTitle = this.getDepartmentTitle(staff.department).toLowerCase();

      const matchesSearch = search === '' || staffName.includes(search) || email.includes(search) || role.includes(search);
      const matchesDepartment = this.directoryDepartmentFilter === '' || String(staff.department) === this.directoryDepartmentFilter || departmentTitle === this.directoryDepartmentFilter.toLowerCase();
      const matchesRole = this.directoryRoleFilter === '' || String(staff.jobTitle || '') === this.directoryRoleFilter;

      return matchesSearch && matchesDepartment && matchesRole;
    });
  }

  clearDirectoryFilters(): void {
    this.directorySearch = '';
    this.directoryDepartmentFilter = '';
    this.directoryRoleFilter = '';
  }

  staffPhotoUrl(staff: HrStaffData): string {
    const passport = this.staffDocumentByType(staff, 'passport');
    const filePath = String(passport?.filePath || '');
    if (/^https?:\/\//i.test(filePath)) {
      return filePath;
    }

    return 'https://ui-avatars.com/api/?name=' + encodeURIComponent(this.staffName(staff)) + '&background=0D8ABC&color=fff&size=96';
  }

  staffDocumentByType(staff: HrStaffData, type: string): any {
    if (!staff || !Array.isArray(staff.documents)) {
      return null;
    }

    return staff.documents.find(document => String(document.documentType || '').toLowerCase() === type.toLowerCase()) || null;
  }

  isStaffOnly(): boolean {
    const rights = this.currentUser?.rights;
    return String(rights?.slug || '').toLowerCase() === 'staff' || String(rights?.adminType || '').toLowerCase() === 'staff';
  }

  organogramRoots(): HrOrganogramData[] {
    const roots = this.organogramList.filter(row => !this.staffRef(row.directBoss || row.supervisor));
    return roots.length > 0 ? roots : this.organogramList;
  }

  organogramChildren(row: any): any[] {
    const staffRef = this.staffRef(row?.staff);
    if (!staffRef) {
      return [];
    }

    const children = this.organogramList.filter(item => this.staffRef(item.directBoss || item.supervisor) === staffRef);
    if (children.length > 0) {
      return children;
    }

    return row.directReports || [];
  }

  staffRef(staff: any): number {
    if (!staff) {
      return 0;
    }

    return Number(staff.ref || staff.staff || 0);
  }

  getDepartmentTitle(value: any): string {
    if (!value) {
      return '';
    }

    if (value.title) {
      return value.title;
    }

    const department = this.departmentList.find(item => String(item.ref) === String(value));
    return department ? department.title : String(value);
  }

  getStaffByRef(value: any): HrStaffData {
    return this.staffList.find(staff => String(staff.ref) === String(value));
  }

  selectedOnboardingStaff(): HrStaffData {
    if (this.selectedOnboardingStaffRef === '') {
      return null;
    }

    return this.getStaffByRef(this.selectedOnboardingStaffRef);
  }

  setOnboardingStaff(event: Event): void {
    this.selectedOnboardingStaffRef = (event.target as HTMLSelectElement).value;
  }

  onboardingDone(staff: HrStaffData, key: string): boolean {
    return !!(staff && staff.onboarding && staff.onboarding[key] === true);
  }

  bankStaffRef(account: HrStaffBankAccountData): any {
    if (!account || !account.staff) {
      return '';
    }

    return typeof account.staff === 'object' ? account.staff.ref : account.staff;
  }

  bankStaffName(account: HrStaffBankAccountData): string {
    if (!account || !account.staff) {
      return '';
    }

    if (typeof account.staff === 'object') {
      return this.staffName(account.staff);
    }

    return this.staffName(this.getStaffByRef(account.staff));
  }

  leaveStaffRef(leave: HrLeaveData): any {
    if (!leave || !leave.staff) {
      return '';
    }

    return typeof leave.staff === 'object' ? leave.staff.ref : leave.staff;
  }

  leaveStaffName(leave: HrLeaveData): string {
    if (!leave || !leave.staff) {
      return '';
    }

    if (typeof leave.staff === 'object') {
      return this.staffName(leave.staff);
    }

    return this.staffName(this.getStaffByRef(leave.staff));
  }

  leaveStatusClass(status: string): string {
    switch (String(status || '').toLowerCase()) {
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      case 'cancelled':
        return 'badge-secondary';
      default:
        return 'badge-warning';
    }
  }

  leaveStatusCount(status: string): number {
    return this.leaveList.filter(item => String(item.status) === status).length;
  }

  performanceStaffRef(review: HrPerformanceData): any {
    if (!review || !review.staff) {
      return '';
    }

    return typeof review.staff === 'object' ? review.staff.ref : review.staff;
  }

  performanceStaffName(review: HrPerformanceData): string {
    if (!review || !review.staff) {
      return '';
    }

    if (typeof review.staff === 'object') {
      return this.staffName(review.staff);
    }

    return this.staffName(this.getStaffByRef(review.staff));
  }

  performanceStatusClass(status: string): string {
    switch (String(status || '').toLowerCase()) {
      case 'approved':
        return 'badge-success';
      case 'reviewed':
        return 'badge-info';
      case 'submitted':
        return 'badge-primary';
      case 'deleted':
        return 'badge-secondary';
      default:
        return 'badge-warning';
    }
  }

  performanceStatusCount(status: string): number {
    return this.performanceList.filter(item => String(item.status) === status).length;
  }

  performanceAverageScore(): number {
    const scoredReviews = this.performanceList.filter(item => Number(item.score) > 0);
    if (scoredReviews.length === 0) {
      return 0;
    }

    const total = scoredReviews.reduce((sum, item) => sum + Number(item.score || 0), 0);
    return Math.round((total / scoredReviews.length) * 10) / 10;
  }

  payrollStaffRef(record: HrPayrollData): any {
    if (!record || !record.staff) {
      return '';
    }

    return typeof record.staff === 'object' ? record.staff.ref : record.staff;
  }

  payrollStaffName(record: HrPayrollData): string {
    if (!record || !record.staff) {
      return '';
    }

    if (typeof record.staff === 'object') {
      return this.staffName(record.staff);
    }

    return this.staffName(this.getStaffByRef(record.staff));
  }

  payrollStatusClass(status: string): string {
    switch (String(status || '').toLowerCase()) {
      case 'paid':
        return 'badge-success';
      case 'processed':
        return 'badge-info';
      case 'cancelled':
        return 'badge-secondary';
      default:
        return 'badge-warning';
    }
  }

  payrollStatusCount(status: string): number {
    return this.payrollList.filter(item => String(item.status) === status).length;
  }

  payrollTotal(field: string): number {
    return this.payrollList.reduce((sum, item) => sum + Number(item[field] || 0), 0);
  }

  expenseStaffRef(expense: HrExpenseData): any {
    if (!expense || !expense.staff) {
      return '';
    }

    return typeof expense.staff === 'object' ? expense.staff.ref : expense.staff;
  }

  expenseStaffName(expense: HrExpenseData): string {
    if (!expense || !expense.staff) {
      return '';
    }

    if (typeof expense.staff === 'object') {
      return this.staffName(expense.staff);
    }

    return this.staffName(this.getStaffByRef(expense.staff));
  }

  expenseStatusClass(status: string): string {
    switch (String(status || '').toLowerCase()) {
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      case 'paid':
        return 'badge-info';
      case 'deleted':
        return 'badge-secondary';
      default:
        return 'badge-warning';
    }
  }

  expenseStatusCount(status: string): number {
    return this.expenseList.filter(item => String(item.status) === status).length;
  }

  expenseTotal(): number {
    return this.expenseList.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  announcementAuthor(announcement: HrAnnouncementData): string {
    if (!announcement?.createdBy) {
      return '';
    }

    return announcement.createdBy.name || [announcement.createdBy.firstName, announcement.createdBy.lastName].filter(Boolean).join(' ') || announcement.createdBy.email || '';
  }

  canManageAnnouncements(): boolean {
    return !this.isStaffOnly() && this.checkService.canWrite(['hr.announcement', 'hr.staff', 'users']);
  }

  canViewHrReports(): boolean {
    const rights = this.currentUser?.rights;
    if (!rights) {
      return false;
    }

    if (rights.super === true || this.checkService.canRead('hr.report')) {
      return true;
    }

    const slug = String(rights.slug || '').toLowerCase();
    const title = String(rights.adminType || '').toLowerCase();
    return ['hr', 'human-resources', 'human_resources', 'head-of-hr', 'head_of_hr', 'coo', 'ceo'].includes(slug)
      || ['hr', 'human resources', 'head of hr', 'coo', 'ceo'].includes(title);
  }

  setReportPeriod(period: 'month' | 'quarter', reload: boolean = true): void {
    const now = new Date();
    let from = new Date(now.getFullYear(), now.getMonth(), 1);
    let to = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    if (period === 'quarter') {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      from = new Date(now.getFullYear(), quarterStartMonth, 1);
      to = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
    }

    this.reportFilterForm.patchValue({
      period,
      dateFrom: this.formatDate(from),
      dateTo: this.formatDate(to),
    });

    if (reload && this.canViewHrReports()) {
      this.getHrReport();
    }
  }

  applyReportFilter(): void {
    this.reportFilterForm.patchValue({ period: 'custom' });
    this.getHrReport();
  }

  exportHrReportExcel(): void {
    if (!this.hrReport) {
      return;
    }

    const html = `
      <html>
        <body>
          <h1>HR Summary Report</h1>
          <p>Generated ${this.escapeHtml(this.hrReport.generatedAt || '')}</p>
          <table>
            <tr><th>Total Headcount</th><td>${this.hrReport.headcountTotal || 0}</td></tr>
            <tr><th>Total Leave Days</th><td>${this.hrReport.leaveDaysTotal || 0}</td></tr>
            <tr><th>Total Expenses</th><td>${this.hrReport.expenseTotal || 0}</td></tr>
          </table>
          ${this.reportTableHtml('Headcount by Department', ['Department', 'Headcount'], (this.hrReport.headcountByDepartment || []).map(row => [row.department, row.headcount]))}
          ${this.reportTableHtml('Leave Usage by Status', ['Status', 'Requests', 'Days'], (this.hrReport.leaveUsage?.byStatus || []).map(row => [row.label, row.requests, row.days]))}
          ${this.reportTableHtml('Leave Usage by Type', ['Type', 'Requests', 'Days'], (this.hrReport.leaveUsage?.byType || []).map(row => [row.label, row.requests, row.days]))}
          ${this.reportTableHtml('Expense Usage by Status', ['Status', 'Requests', 'Amount'], (this.hrReport.expenseUsage?.byStatus || []).map(row => [row.label, row.requests, row.amount]))}
          ${this.reportTableHtml('Expense Usage by Category', ['Category', 'Requests', 'Amount'], (this.hrReport.expenseUsage?.byCategory || []).map(row => [row.label, row.requests, row.amount]))}
        </body>
      </html>`;
    this.downloadText('hr-summary-report.xls', 'application/vnd.ms-excel;charset=utf-8;', html);
  }

  exportHrReportPdf(): void {
    if (!this.hrReport) {
      return;
    }

    const html = `
      <html>
        <head>
          <title>HR Summary Report</title>
          <style>
            body { font-family: Arial, sans-serif; color: #222; margin: 24px; }
            h1 { margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin: 18px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f1f3f5; }
            .summary { display: flex; gap: 24px; margin: 18px 0; }
            .summary div { border: 1px solid #ddd; padding: 12px; min-width: 160px; }
            .summary strong { display: block; font-size: 22px; }
          </style>
        </head>
        <body>
          <h1>HR Summary Report</h1>
          <p>${this.escapeHtml(this.hrReport.dateFrom || '')} to ${this.escapeHtml(this.hrReport.dateTo || '')} · Generated ${this.escapeHtml(this.hrReport.generatedAt || '')}</p>
          <div class="summary">
            <div><strong>${this.hrReport.headcountTotal || 0}</strong><span>Total headcount</span></div>
            <div><strong>${this.hrReport.leaveDaysTotal || 0}</strong><span>Total leave days</span></div>
            <div><strong>${this.hrReport.expenseTotal || 0}</strong><span>Total expenses</span></div>
          </div>
          ${this.reportTableHtml('Headcount by Department', ['Department', 'Headcount'], (this.hrReport.headcountByDepartment || []).map(row => [row.department, row.headcount]))}
          ${this.reportTableHtml('Leave Usage by Status', ['Status', 'Requests', 'Days'], (this.hrReport.leaveUsage?.byStatus || []).map(row => [row.label, row.requests, row.days]))}
          ${this.reportTableHtml('Leave Usage by Type', ['Type', 'Requests', 'Days'], (this.hrReport.leaveUsage?.byType || []).map(row => [row.label, row.requests, row.days]))}
          ${this.reportTableHtml('Expense Usage by Status', ['Status', 'Requests', 'Amount'], (this.hrReport.expenseUsage?.byStatus || []).map(row => [row.label, row.requests, row.amount]))}
          ${this.reportTableHtml('Expense Usage by Category', ['Category', 'Requests', 'Amount'], (this.hrReport.expenseUsage?.byCategory || []).map(row => [row.label, row.requests, row.amount]))}
        </body>
      </html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  }

  previousLeaveMonth(): void {
    this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() - 1, 1);
    this.getLeaveCalendar();
  }

  nextLeaveMonth(): void {
    this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 1);
    this.getLeaveCalendar();
  }

  leaveCalendarTitle(): string {
    return this.calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  leaveCalendarWeeks(): any[][] {
    const range = this.calendarRange();
    const days = [];
    const current = new Date(range.first);

    while (current <= range.last) {
      const date = new Date(current);
      days.push({
        date,
        label: date.getDate(),
        inMonth: date.getMonth() === this.calendarDate.getMonth(),
        leaves: this.leavesForDay(date),
      });
      current.setDate(current.getDate() + 1);
    }

    const weeks = [];
    for (let index = 0; index < days.length; index += 7) {
      weeks.push(days.slice(index, index + 7));
    }

    return weeks;
  }

  leavesForDay(day: Date): HrLeaveData[] {
    const dayValue = this.formatDate(day);
    return this.calendarLeaveList.filter(leave => {
      return String(leave.status) !== 'deleted' && leave.startDate <= dayValue && leave.endDate >= dayValue;
    });
  }

  private calendarRange(): { first: Date, last: Date } {
    const firstOfMonth = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth(), 1);
    const lastOfMonth = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 0);
    const first = new Date(firstOfMonth);
    const last = new Date(lastOfMonth);

    first.setDate(first.getDate() - first.getDay());
    last.setDate(last.getDate() + (6 - last.getDay()));

    return { first, last };
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return year+'-'+month+'-'+day;
  }

  private splitCsv(value: string): string[] {
    if (!value) {
      return [];
    }

    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }

  private getFile(event: Event): File {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return null;
    }

    return input.files[0];
  }

  private openModal(modalId: string): void {
    setTimeout(() => {
      const trigger = document.querySelector('[data-target="#'+modalId+'"]') as HTMLElement;
      if (trigger) {
        trigger.click();
      }
    });
  }

  private closeModal(modalId: string): void {
    const closeButton = document.querySelector('#'+modalId+' [data-dismiss="modal"]') as HTMLElement;
    if (closeButton) {
      closeButton.click();
    }
  }

  private downloadText(filename: string, mimeType: string, text: string): void {
    const blob = new Blob([text], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private reportTableHtml(title: string, headers: string[], rows: any[][]): string {
    const headerHtml = headers.map(header => '<th>'+this.escapeHtml(header)+'</th>').join('');
    const rowHtml = rows.length > 0
      ? rows.map(row => '<tr>'+row.map(cell => '<td>'+this.escapeHtml(cell)+'</td>').join('')+'</tr>').join('')
      : '<tr><td colspan="'+headers.length+'">No records found.</td></tr>';

    return '<h2>'+this.escapeHtml(title)+'</h2><table><thead><tr>'+headerHtml+'</tr></thead><tbody>'+rowHtml+'</tbody></table>';
  }

  private showApiError(data: any, fallback: string): void {
    const message = data?.error?.additional_message ? data.error.message + '. ' + data.error.additional_message : data?.error?.message || data?.message || fallback;
    this.notifyService.showError(message, 'Error');
  }
}
